(ns profile.events
  (:require
   [very.re-frame.core :as rf :refer [<sub >evt]]
   [oops.core :refer [oget]]
   [camel-snake-kebab.core :as k]
   [util.core :refer [transform-keys]]
   [runtime.core :refer [env]]
   [graphql.core :as qu]))

(rf/reg-event-fx
 :profile/navigate-to-context
 (fn [cfx [data]]
   {:db (assoc-in (:db cfx) [:profile :context] data)}))

(defn uploadcare-file [photo-url]
  ((oget js/uploadcare "fileFrom") "uploaded" photo-url))

(defn uploadcare-dialog [photo-url]
  (let [file (if photo-url (uploadcare-file photo-url) nil)]
    ((oget js/uploadcare "openDialog") file
                                       (clj->js {:imagesOnly true
                                                 :imageShrink "800x800"
                                                 :crop "1:1"}))))

(defn load-uploadcare-key [cfx]
  (let [loaded? (get-in cfx [:db :uploadcare :script-loaded?])
        key (:opencare-public-key (env))]
    (when-not loaded?
      (set! js/UPLOADCARE_PUBLIC_KEY key))))

(rf/reg-fx
 :open-uploadcare
 (fn [photo-url]
   ((oget (uploadcare-dialog photo-url) "done")
    (fn [file]
      ((oget file "done")
       #(>evt [:uploadcare-complete
               (transform-keys k/->kebab-case-keyword
                               (js->clj % :keywordize-keys true))]))))))

(rf/reg-event-fx
 :load-uploadcare
 [(rf/after load-uploadcare-key)]
 (fn [cfx]
   (let [loaded? (get-in cfx [:db :uploadcare :script-loaded?])
         photo-url (get-in cfx [:db :personal-information :photo-url])]
     (if loaded?
       {:open-uploadcare photo-url}
       {:insert-script {:src "https://ucarecdn.com/libs/widget/3.5.1/uploadcare.full.min.js"
                        :on-load [:uploadcare-script-loaded photo-url]}}))))

(rf/reg-event-fx
 :uploadcare-complete
 (fn [cfx [{:keys [uuid cdn-url-modifiers cdn-url] :as data}]]
   {:db (-> (:db cfx)
            (assoc-in [:personal-information :photo-url] cdn-url))
    :graphql {:gql (qu/update-profile-photo
                    {:input {:url cdn-url :uuid uuid :url-modifiers cdn-url-modifiers}})
              :success [:success-message "Photo updated"]}}))

(defn- load-flow []
  {:first-dispatch [:fetch-client-profile]
   :rules [{:when :seen-all-of?
            :events [:client-profile-fetched
                     :risk-profile/fetched
                     :priorities/fetched
                     :income/fetched
                     :expenses/fetched
                     :assets/fetched
                     :liabilities/fetched
                     :insurance/fetched
                     :estate/fetched
                     :tax/fetched]
            :dispatch [:loader/hide]
            :halt? true}
           {:when :seen-any-of?
            :events [:client-profile-error
                     :risk-profile/fetch-errored
                     :priorities/fetch-errored
                     :income/fetch-errored
                     :expenses/fetch-errored
                     :assets/fetch-errored
                     :liabilities/fetch-errored
                     :insurance/fetch-errored
                     :estate/fetch-errored
                     :tax/fetch-errored]
            :dispatch [:loader/hide]
            :halt? true}]})

(rf/reg-event-fx
 :boot-client-profile
 (fn [cfx]
   {:dispatch [:loader/show]
    :db (assoc-in (:db cfx) [:profile :context] :view-profile)
    :async-flow (load-flow)}))

(rf/reg-event-fx
 :fetch-client-profile
 (fn [cfx]
   {:graphql {:gql (qu/client-profile)
              :success [:client-profile-fetched]
              :error [:client-profile-error]
              :cancellable? true}
    :entity [{:type :risk-profile
              :op :get}
             {:type :priorities
              :op :get}
             {:type :income
              :op :get}
             {:type :expenses
              :op :get}
             {:type :assets
              :op :get}
             {:type :liabilities
              :op :get}
             {:type :insurance
              :op :get}
             {:type :estate
              :op :get}
             {:type :tax
              :op :get}]}))

(rf/reg-sub
 :personal-information
 (fn [db]
   (:personal-information db)))

(rf/reg-event-fx
 :profile/update-insurance
 (fn [cfx [policies]]
   {:entity {:type :insurance
             :op :patch
             :data policies
             :success [:profile/insurance-updated]
             :error [:profile/common-failed]}}))

(rf/reg-event-fx
 :profile/insurance-updated
 (fn [cfx]
   {:dispatch-n [[:success-message "You've successfully updated your insurance policies"]
                 [:profile/navigate-to-context :view-profile]]}))

(rf/reg-event-fx
 :profile/edit-assets-and-liabilities
 (fn [cfx]
   {:dispatch [:profile/navigate-to-context :edit-assets-and-liabilities]}))

(rf/reg-event-fx
 :profile/update-assets-and-liabilities
 (fn [cfx [{:keys [assets liabilities]}]]
   {:async-flow {:id :profile/a&l
                 :rules [{:when :seen-both?
                          :events [:assets/updated
                                   :liabilities/updated]
                          :dispatch [:profile/assets-and-liabilities-updated]
                          :halt? true}
                         ;; clean up after ourselves if the user moves away
                         ;; from editing assets/liabilities
                         {:when :seen-any-of?
                          :events [:navigate-to-context
                                   :router/route-to-path]
                          :halt? true}
                         {:when :seen-any-of?
                          :events [:assets/update-errored
                                   :liabilities/update-errored]
                          :dispatch [:profile/common-failed]
                          :halt? true}]}
    :entity [{:type :assets
              :op :patch
              :data assets}
             {:type :liabilities
              :op :patch
              :data liabilities}]}))

(rf/reg-event-fx
 :profile/assets-and-liabilities-updated
 (fn [cfx]
   {:dispatch-n [[:success-message "You've successfully updated your assets and liabilities"]
                 [:profile/navigate-to-context :view-profile]]}))

(defn client-profile-fetched [db [data]]
  (-> db
      (assoc :personal-information (get-in data [:client :personal-information]))))

(rf/reg-event-db
 :client-profile-fetched
 client-profile-fetched)

(rf/reg-event-db
 :client-profile-error
 (fn [db [data]]
   (client-profile-fetched (assoc-in db [:profile :fetch-error] true) [(:data data)])))

(rf/reg-sub
 :profile/fetch-error
 (comp :fetch-error :profile))

(rf/reg-sub
 :profile/assets-total
 :<- [:assets]
 (fn [assets]
   (reduce #(+ %1 (:value %2)) 0 assets)))

(rf/reg-sub
 :profile/insurance-policies-total
 :<- [:insurance]
 (fn [policies]
   (reduce #(+ %1 (:amount %2)) 0 policies)))

(rf/reg-sub
 :profile/liabilities-total
 :<- [:liabilities]
 (fn [liabilities]
   (reduce #(+ %1 (:value %2)) 0 liabilities)))

(rf/reg-event-fx
 :personal-information-updated
 (fn [cfx [{:keys [update-personal-information] :as data}]]
   {:db (assoc (:db cfx) :personal-information update-personal-information)
    :dispatch-n [[:success-message "You've successfully completed your personal information"]
                 [:profile/navigate-to-context :view-profile]]}))

(rf/reg-event-fx
 :profile/common-failed
 (fn [_cfx [data]]
   {:dispatch-n [[:profile/navigate-to-context :view-profile]
                 [:success-message "There was a problem updating your information"]]}))

(rf/reg-event-fx
 :update-personal-information
 (fn [_cfx [data]]
   (let [data (select-keys data [:children
                                 :children-count
                                 :city
                                 :dob
                                 :employer
                                 :id
                                 :marital-status
                                 :occupation
                                 :postal-code
                                 :retired
                                 :retirement-age
                                 :spouse
                                 :state
                                 :street])
         trimmed (-> data
                     (#(if-not (contains? #{"Married" "Domestic-partnership"} (:marital-status %)) (dissoc % :spouse) %))
                     (#(if (> (:children-count %) 0)
                         (assoc % :children (take (int (:children-count %)) (:children %)))
                         (dissoc % :children))))]
     {:graphql {:gql (qu/update-personal-information
                      {:input trimmed})
                :success [:personal-information-updated]
                :error [:profile/common-failed]}})))

(rf/reg-event-fx
 :profile/edit-risk-survey
 (fn [cfx]
   {:prevent-events {:register :profile/edit-risk-survey
                     :catch-these-events #{:navigate-to-context
                                           :router/route-to-path}
                     :and-instead-dispatch [:profile/warn-on-leave]}
    :dispatch [:profile/navigate-to-context :edit-risk-survey]}))

(rf/reg-event-fx
 :profile/update-risk-survey
 (fn [_cfx [data]]
   {:entity {:type :risk-profile
             :op :patch
             :data (select-keys data [:growth :concerns :knowledge
                                      :tradeoff :loss-anxiety
                                      :timeframe :expenses])
             :success [:profile/risk-survey-updated]
             :error [:profile/common-failed]}}))

(rf/reg-event-fx
 :profile/risk-survey-updated
 (fn [cfx [_]]
   {:prevent-events {:unregister :profile/edit-risk-survey}
    :dispatch-n [[:success-message "You've successfully completed your Risk Tolerance Profile"]
                 [:profile/navigate-to-context :view-profile]]}))

(rf/reg-event-fx
 :profile/cancel-edit-risk-survey
 (fn [cfx]
   {:prevent-events {:unregister :profile/edit-risk-survey}
    :dispatch [:profile/navigate-to-context :view-profile]}))

(rf/reg-event-fx
 :profile/warn-on-leave
 (fn [_]
   {:modal {:open 'profile.views/navigation-confirmation-modal}}))

(rf/reg-event-fx
 :profile/cancel-leave-survey
 (fn [cfx]
   {:modal {:close 'profile.views/navigation-confirmation-modal}}))

(rf/reg-event-fx
 :profile/leave-survey
 (fn [cfx]
   {:modal {:close 'profile.views/navigation-confirmation-modal}
    :prevent-events {:unregister :profile/edit-risk-survey}}))

(rf/reg-event-fx
 :uploadcare-script-loaded
 (fn [cfx [photo-url]]
   {:db (assoc-in (:db cfx) [:uploadcare :script-loaded?] true)
    :open-uploadcare photo-url}))

(rf/reg-sub
 :profile
 (fn [db]
   (:profile db)))

(rf/reg-sub
 :profile/context
 :<- [:profile]
 (fn [prof]
   (:context prof)))

(rf/reg-sub
 :uploadcare
 (fn [db]
   (:uploadcare db)))

(rf/reg-sub
 :profile/estate-and-tax
 :<- [:estate]
 :<- [:tax]
 (fn [[estate tax]]
   {:estate estate
    :tax tax}))

(rf/reg-event-fx
 :profile/update-estate-and-tax
 (fn [cfx [{:keys [estate tax]}]]
   {:entity [{:type :estate
              :op :patch
              :data estate}
             {:type :tax
              :op :patch
              :data tax}]
    :async-flow {:id :profile/estate&tax
                 :rules [{:when :seen-both?
                          :events [:estate/updated
                                   :tax/updated]
                          :dispatch [:profile/estate-and-tax-updated]
                          :halt? true}
                         ;; clean up after ourselves if the user moves away
                         {:when :seen-any-of?
                          :events [:navigate-to-context
                                   :router/route-to-path]
                          :halt? true}
                         {:when :seen-any-of?
                          :events [:estate/update-errored
                                   :tax/update-errored]
                          :dispatch [:profile/common-failed]
                          :halt? true}]}}))

(rf/reg-event-fx
 :profile/estate-and-tax-updated
 (fn [cfx]
   {:dispatch-n [[:success-message "You've successfully updated your estate & tax information"]
                 [:profile/navigate-to-context :view-profile]]}))

(rf/reg-event-fx
 :profile/update-expenses
 (fn [cfx [expenses]]
   {:entity {:type :expenses
             :op :patch
             :data expenses
             :success [:profile/expenses-updated]
             :error [:profile/common-failed]}}))


(rf/reg-event-fx
 :profile/expenses-updated
 (fn [cfx]
   {:dispatch-n [[:success-message "You've successfully updated your expenses"]
                 [:profile/navigate-to-context :view-profile]]}))

(rf/reg-sub
 :profile/total-expenses
 :<- [:expenses]
 (fn [exp]
   (let [total (reduce + 0 (vals exp))]
     (if (zero? total)
       nil
       total))))

(rf/reg-event-fx
 :profile/update-income
 (fn [_cfx [data]]
   {:entity {:type :income
             :op :patch
             :data (select-keys data [:client :spouse])
             :success [:profile/income-updated]
             :error [:profile/common-failed]}}))

(rf/reg-event-fx
 :profile/income-updated
 (fn [cfx]
   {:dispatch-n [[:success-message "You've successfully updated your income"]
                 [:profile/navigate-to-context :view-profile]]}))

(rf/reg-event-fx
  :profile/edit-priorities
  (fn [cfx]
    {:prevent-events {:register :priorities/prevent
                      :catch-these-events #{:navigate-to-context
                                            :router/route-to-path
                                            :profile/navigate-to-context}
                      :and-instead-dispatch [:profile/leave-edit-priorities]}
     :dispatch [:profile/navigate-to-context :manage-priorities]}))

(rf/reg-event-fx
 :profile/leave-edit-priorities
 (fn [cfx]
   {:prevent-events {:unregister :priorities/prevent}
    :dispatch [:financial-priorities/reset]}))

(rf/reg-event-fx
 :profile/update-priorities
 (fn [cfx [priorities]]
   {:entity {:type :priorities
             :op :patch
             :data priorities
             :success [:profile/updated-priorities]
             :error [:profile/updated-priorities]}}))


(rf/reg-event-fx
 :profile/updated-priorities
 (fn [cfx]
   {:prevent-events {:unregister :priorities/prevent}
    :dispatch-n [[:success-message "You've successfully updated your financial priorities"]
                 [:financial-priorities/reset]
                 [:profile/navigate-to-context :view-profile]]}))
